import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { CalendarCheck, ClipboardList, MessageCircle, BookOpen, Sparkles, ShieldCheck, Gift, Copy, Crown } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

export default function Dashboard() {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [vouchers, setVouchers] = useState([]);
  const [issuing, setIssuing] = useState(false);
  const [rebelList, setRebelList] = useState({ on_list: false, joined_at: null });
  const [rlBusy, setRlBusy] = useState(false);

  const load = () => Promise.all([
    api.get("/bookings").then(r => setBookings(r.data)),
    api.get("/vouchers").then(r => setVouchers(r.data)).catch(() => setVouchers([])),
    api.get("/rebel-list/me").then(r => setRebelList(r.data)).catch(() => setRebelList({ on_list: false })),
  ]);
  useEffect(() => { load(); }, []);

  const toggleRebelList = async () => {
    setRlBusy(true);
    try {
      if (rebelList.on_list) {
        const { data } = await api.delete("/rebel-list/me");
        setRebelList({ on_list: false, joined_at: null });
        toast.success(data.message || "You've left The Rebel List.");
      } else {
        const { data } = await api.post("/rebel-list/me");
        setRebelList({ on_list: true, joined_at: data.joined_at });
        toast.success(data.message || "You're on The Rebel List.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update your list status.");
    } finally { setRlBusy(false); }
  };

  const hasApproved = bookings.some(b => b.status === "confirmed");
  const activeVoucher = vouchers.find(v => v.status === "active");

  const issue = async () => {
    setIssuing(true);
    try {
      const { data } = await api.post("/vouchers");
      toast.success("Voucher created. Send it to a friend.");
      setVouchers([data, ...vouchers]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create voucher.");
    } finally { setIssuing(false); }
  };

  const copy = (code) => {
    navigator.clipboard?.writeText(code);
    toast.success("Copied.");
  };

  const payBalance = async (booking_id) => {
    try {
      const { data } = await api.post(`/bookings/${booking_id}/pay-balance`, { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not open checkout.");
    }
  };
  const tiles = [
    { to: "/dashboard/intake", icon: ClipboardList, t: "Intake", d: "Update your discreet questionnaire.", test: "tile-intake" },
    { to: "/dashboard/book", icon: CalendarCheck, t: "Book a Session", d: "Pick a city + time.", test: "tile-book" },
    { to: "/dashboard/journal", icon: BookOpen, t: "Private Journal", d: "Write. Reflect with AI.", test: "tile-journal" },
    { to: "/dashboard/messages", icon: MessageCircle, t: "Messages", d: "Direct to Rebel.", test: "tile-messages" },
  ];
  return (
    <div data-testid="dashboard-page" className="max-w-6xl mx-auto px-6 sm:px-10 py-16">
      <div className="eyebrow-lg text-center">Your Private Client Experience</div>
      <h1 className="mt-8 h-editorial text-5xl sm:text-7xl text-center">Hello, <span className="italic-serif text-[#D4AF37]">{user?.name || "friend"}.</span></h1>

      {/* JOURNEY STAGES */}
      <div className="mt-20 grid grid-cols-1 md:grid-cols-4 gap-4 text-center">
        {[
          { k: "You're Booked", d: bookings.length > 0 ? "Confirmed" : "Not yet", to: "/dashboard/book", test: "stage-booked" },
          { k: "Before We Meet", d: "Intake & consent", to: "/dashboard/intake", test: "stage-before" },
          { k: "Your Experience", d: "Messaging", to: "/dashboard/messages", test: "stage-experience" },
          { k: "Afterward", d: "Reflection journal", to: "/dashboard/journal", test: "stage-after" },
        ].map((s, i) => (
          <Link key={s.k} to={s.to} data-testid={s.test} className="hairline p-6 hover:border-[#00D1FF]/40 transition-colors group text-left">
            <div className="eyebrow-lg text-zinc-500">Stage 0{i + 1}</div>
            <div className="mt-5 font-serif-editorial text-2xl group-hover:text-[#D4AF37] transition-colors">{s.k}</div>
            <div className="mt-2 text-xs text-zinc-500">{s.d}</div>
          </Link>
        ))}
      </div>

      {/* APPOINTMENTS */}
      <div className="mt-24">
        <div className="eyebrow-lg text-center">Your Appointments</div>
        {bookings.length === 0 ? (
          <div className="mt-12 hairline p-16 text-center">
            <Sparkles className="w-6 h-6 text-[#D4AF37] mx-auto mb-6" />
            <h2 className="h-editorial text-3xl">Nothing on the calendar <span className="italic-serif text-[#D4AF37]">yet.</span></h2>
            <p className="mt-4 text-zinc-500 max-w-md mx-auto">Pick a city. Choose an experience. Come curious.</p>
            <Link to="/experiences" data-testid="dashboard-book-cta" className="mt-8 rc-btn-primary inline-block">Explore Experiences</Link>
          </div>
        ) : (
          <div className="mt-12 space-y-3">
            {bookings.map(b => (
              <div key={b.booking_id} data-testid={`booking-${b.booking_id}`} className="hairline p-8 flex flex-wrap items-center gap-6 justify-between">
                <div>
                  <div className="font-serif-editorial text-3xl">{b.city_name}</div>
                  <div className="mt-2 eyebrow-lg text-zinc-500">{b.package_name}</div>
                  <div className="text-sm text-zinc-400 mt-3">{new Date(b.slot_iso).toLocaleString(undefined, { weekday: "long", month: "long", day: "numeric", hour: "numeric", minute: "2-digit" })}</div>
                  {b.payment_option === "deposit" && b.balance_status === "pending" && (
                    <div className="mt-3 text-xs text-[#D4AF37] font-mono-eyebrow uppercase tracking-[0.24em]">
                      Balance ${(b.balance_amount/100).toLocaleString()} due {b.balance_due_at ? new Date(b.balance_due_at).toLocaleDateString() : "soon"}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  <span className={`text-[10px] font-mono-eyebrow uppercase tracking-[0.28em] px-3 py-1.5 rounded-full border
                    ${b.status === "confirmed" ? "text-[#00E699] border-[#00E699]/40" :
                      b.status === "pending_approval" ? "text-[#D4AF37] border-[#D4AF37]/40" :
                      b.status === "declined" ? "text-red-400 border-red-400/40" :
                      b.status === "refunded" ? "text-red-400 border-red-400/40" :
                      "text-zinc-400 border-zinc-600"}`}>
                    {b.status.replace(/_/g, " ")}
                  </span>
                  <span className="text-sm text-zinc-500">${(b.amount/100).toLocaleString()}</span>
                  {b.payment_option === "deposit" && b.balance_status === "pending" && (
                    <button data-testid={`pay-balance-${b.booking_id}`} onClick={() => payBalance(b.booking_id)} className="rc-btn-gold text-[10px]">Pay Balance</button>
                  )}
                </div>
              </div>
            ))}
            <div className="mt-8 text-center">
              <Link to="/experiences" data-testid="book-again-cta" className="rc-btn-gold inline-block">Book Again →</Link>
            </div>
          </div>
        )}
      </div>

      {/* REBEL LIST LEDGER */}
      <div data-testid="rebel-list-ledger" className="mt-20 hairline p-8 sm:p-10 flex flex-col sm:flex-row items-start sm:items-center gap-6 justify-between">
        <div className="flex items-start gap-4">
          <Crown className={`w-5 h-5 mt-1 ${rebelList.on_list ? "text-[#D4AF37]" : "text-zinc-600"}`} />
          <div>
            <div className="eyebrow-lg text-[#D4AF37]">The Rebel List</div>
            <div className="mt-3 font-serif-editorial text-2xl sm:text-3xl">
              {rebelList.on_list ? (
                <>You're <span className="italic-serif text-[#D4AF37]">on the list.</span></>
              ) : (
                <>You're <span className="italic-serif text-zinc-500">not on the list yet.</span></>
              )}
            </div>
            <p className="mt-3 text-sm text-zinc-500 max-w-md">
              {rebelList.on_list
                ? "First to know when a city opens. First 24 hours to book, before public. Leave anytime — no notice needed."
                : "24-hour first access to new city dates before public booking. One-tap in, one-tap out."}
            </p>
            {rebelList.on_list && rebelList.joined_at && (
              <div className="mt-3 text-[10px] font-mono-eyebrow uppercase tracking-[0.28em] text-zinc-600">
                Joined {new Date(rebelList.joined_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
              </div>
            )}
          </div>
        </div>
        <button
          data-testid={rebelList.on_list ? "rebel-list-leave-btn" : "rebel-list-join-btn"}
          onClick={toggleRebelList}
          disabled={rlBusy}
          className={`${rebelList.on_list ? "rc-btn-ghost" : "rc-btn-gold"} disabled:opacity-50 whitespace-nowrap`}
        >
          {rlBusy ? "…" : rebelList.on_list ? "Leave the list" : "Join the list"}
        </button>
      </div>

      {/* PRIVACY PROMISE */}
      <div className="mt-24 text-center hairline p-10">
        <div className="eyebrow-lg text-[#D4AF37]">Private by Design</div>
        <p className="mt-6 h-editorial italic-serif text-2xl sm:text-3xl text-zinc-300">What you explore is yours.</p>
        <p className="mt-6 text-xs text-zinc-500 flex items-center justify-center gap-2 font-mono-eyebrow uppercase tracking-[0.28em]">
          <ShieldCheck className="w-4 h-4 text-[#D4AF37]" /> Tap the gold Exit button at any time
        </p>
      </div>

      {/* REFERRAL VOUCHER */}
      <div data-testid="voucher-card" className="mt-16 hairline p-10 text-center">
        <Gift className="w-5 h-5 text-[#D4AF37] mx-auto" />
        <div className="mt-4 eyebrow-lg">Gift a Friend</div>
        <h2 className="mt-6 h-editorial text-3xl sm:text-4xl">Send someone <span className="italic-serif text-[#D4AF37]">a first Spark.</span></h2>
        <p className="mt-4 max-w-lg mx-auto text-sm text-zinc-400">After your first confirmed session, you can generate one voucher worth $150 off. Share it with someone whose curiosity you want to honor.</p>

        {!hasApproved ? (
          <p className="mt-8 text-xs text-zinc-500 font-mono-eyebrow uppercase tracking-[0.24em]">Unlocks after your first confirmed experience.</p>
        ) : activeVoucher ? (
          <div className="mt-8 max-w-md mx-auto hairline p-5 bg-[#D4AF37]/5 border-[#D4AF37]/30 flex items-center justify-between gap-3 text-left">
            <div>
              <div className="font-mono-eyebrow tracking-[0.28em] text-xl text-[#D4AF37]" data-testid="active-voucher-code">{activeVoucher.code}</div>
              <div className="mt-1 text-xs text-zinc-500">${(activeVoucher.amount_cents/100).toFixed(0)} off, redeemable at booking</div>
            </div>
            <button data-testid="voucher-copy" onClick={() => copy(activeVoucher.code)} className="rc-btn-ghost inline-flex items-center gap-2"><Copy className="w-3 h-3" /> Copy</button>
          </div>
        ) : (
          <button data-testid="voucher-generate" onClick={issue} disabled={issuing} className="mt-8 rc-btn-gold disabled:opacity-50">{issuing ? "Creating…" : "Generate Voucher"}</button>
        )}
      </div>
    </div>
  );
}
